import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Trophy, GamepadIcon, Heart, History, LogOut, Edit2, Check, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface GameStat {
  game_id: string;
  games_played: number;
  wins: number;
  total_score: number;
  best_score: number;
}

interface Favorite {
  game_id: string;
  created_at: string;
}

interface GameHistoryItem {
  id: string;
  game_id: string;
  score: number;
  result: string | null;
  played_at: string;
}

const GAME_NAMES: Record<string, string> = {
  'loup-garou': 'Loup-Garou',
  'impro': 'Impro Théâtrale',
  'tribunal': 'Le Tribunal',
  'appel-absurde': 'Appel Absurde',
  'murder-mystery': 'Murder Mystery',
  'rpg-ia': 'Aventure RPG',
};

const GAME_EMOJIS: Record<string, string> = {
  'loup-garou': '🐺',
  'impro': '🎭',
  'tribunal': '⚖️',
  'appel-absurde': '📞',
  'murder-mystery': '🔍',
  'rpg-ia': '🗡️',
};

const Profile = () => {
  const { user, profile, loading, signOut, updateProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  
  const [stats, setStats] = useState<GameStat[]>([]);
  const [favorites, setFavorites] = useState<Favorite[]>([]);
  const [history, setHistory] = useState<GameHistoryItem[]>([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editUsername, setEditUsername] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!loading && !user) {
      navigate('/auth');
    }
  }, [user, loading, navigate]);

  useEffect(() => {
    if (user) {
      fetchUserData();
    }
  }, [user]);

  const fetchUserData = async () => {
    if (!user) return;

    // Fetch stats
    const { data: statsData } = await supabase
      .from('game_stats')
      .select('*')
      .eq('user_id', user.id);
    if (statsData) setStats(statsData);

    // Fetch favorites
    const { data: favData } = await supabase
      .from('favorites')
      .select('*')
      .eq('user_id', user.id);
    if (favData) setFavorites(favData);

    // Fetch history
    const { data: historyData } = await supabase
      .from('game_history')
      .select('*')
      .eq('user_id', user.id)
      .order('played_at', { ascending: false })
      .limit(10);
    if (historyData) setHistory(historyData);
  };

  const handleSaveUsername = async () => {
    if (!editUsername.trim()) return;
    
    setIsSaving(true);
    const { error } = await updateProfile({ username: editUsername.trim() });
    
    if (error) {
      toast({
        title: 'Erreur',
        description: 'Impossible de modifier le pseudo.',
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Profil mis à jour',
        description: 'Votre pseudo a été modifié.',
      });
      setIsEditing(false);
    }
    setIsSaving(false);
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const totalGamesPlayed = stats.reduce((sum, s) => sum + s.games_played, 0);
  const totalWins = stats.reduce((sum, s) => sum + s.wins, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Mon Profil - SuperSociety.ai</title>
      </Helmet>

      <div className="min-h-screen bg-background relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 pointer-events-none">
          <div 
            className="absolute top-[-20%] left-1/2 -translate-x-1/2 w-[1000px] h-[1000px] rounded-full opacity-20"
            style={{
              background: 'radial-gradient(circle, hsl(var(--accent) / 0.3) 0%, transparent 60%)',
            }}
          />
        </div>

        <div className="relative z-10 container mx-auto px-4 py-8 max-w-4xl">
          {/* Header */}
          <header className="flex items-center justify-between mb-8">
            <Link to="/" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Retour
            </Link>
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </header>

          {/* Profile Card */}
          <div className="p-6 rounded-2xl bg-card/50 border border-border/50 backdrop-blur-sm mb-8">
            <div className="flex items-center gap-6">
              <div className="w-20 h-20 rounded-full bg-accent/20 border-2 border-accent/40 flex items-center justify-center">
                {profile?.avatar_url ? (
                  <img src={profile.avatar_url} alt="Avatar" className="w-full h-full rounded-full object-cover" />
                ) : (
                  <User className="w-10 h-10 text-accent" />
                )}
              </div>
              
              <div className="flex-1">
                {isEditing ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={editUsername}
                      onChange={(e) => setEditUsername(e.target.value)}
                      className="max-w-xs"
                      placeholder="Nouveau pseudo"
                    />
                    <Button size="icon" variant="ghost" onClick={handleSaveUsername} disabled={isSaving}>
                      {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 text-green-500" />}
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => setIsEditing(false)}>
                      <X className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <h1 className="font-display text-2xl text-foreground">
                      {profile?.username || 'Joueur'}
                    </h1>
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      onClick={() => {
                        setEditUsername(profile?.username || '');
                        setIsEditing(true);
                      }}
                    >
                      <Edit2 className="w-4 h-4 text-muted-foreground" />
                    </Button>
                  </div>
                )}
                <p className="text-sm text-muted-foreground">{user?.email}</p>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-border/50">
              <div className="text-center">
                <div className="text-2xl font-bold text-foreground">{totalGamesPlayed}</div>
                <div className="text-sm text-muted-foreground">Parties jouées</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-accent">{totalWins}</div>
                <div className="text-sm text-muted-foreground">Victoires</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-moon">{favorites.length}</div>
                <div className="text-sm text-muted-foreground">Favoris</div>
              </div>
            </div>
          </div>

          {/* Stats by Game */}
          <section className="mb-8">
            <h2 className="font-display text-xl text-foreground mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-accent" />
              Statistiques par jeu
            </h2>
            
            {stats.length === 0 ? (
              <div className="p-6 rounded-xl bg-card/30 border border-border/30 text-center text-muted-foreground">
                Aucune partie jouée pour le moment
              </div>
            ) : (
              <div className="grid gap-3">
                {stats.map((stat) => (
                  <div 
                    key={stat.game_id}
                    className="p-4 rounded-xl bg-card/30 border border-border/30 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{GAME_EMOJIS[stat.game_id] || '🎮'}</span>
                      <div>
                        <div className="font-medium text-foreground">{GAME_NAMES[stat.game_id] || stat.game_id}</div>
                        <div className="text-sm text-muted-foreground">
                          {stat.games_played} parties • {stat.wins} victoires
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-accent">{stat.total_score}</div>
                      <div className="text-xs text-muted-foreground">points</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Favorites */}
          <section className="mb-8">
            <h2 className="font-display text-xl text-foreground mb-4 flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-400" />
              Jeux favoris
            </h2>
            
            {favorites.length === 0 ? (
              <div className="p-6 rounded-xl bg-card/30 border border-border/30 text-center text-muted-foreground">
                Aucun favori ajouté
              </div>
            ) : (
              <div className="flex flex-wrap gap-3">
                {favorites.map((fav) => (
                  <Link
                    key={fav.game_id}
                    to={`/${fav.game_id}`}
                    className="px-4 py-2 rounded-full bg-pink-500/10 border border-pink-500/30 text-pink-400 hover:bg-pink-500/20 transition-colors flex items-center gap-2"
                  >
                    <span>{GAME_EMOJIS[fav.game_id] || '🎮'}</span>
                    {GAME_NAMES[fav.game_id] || fav.game_id}
                  </Link>
                ))}
              </div>
            )}
          </section>

          {/* History */}
          <section>
            <h2 className="font-display text-xl text-foreground mb-4 flex items-center gap-2">
              <History className="w-5 h-5 text-muted-foreground" />
              Historique récent
            </h2>
            
            {history.length === 0 ? (
              <div className="p-6 rounded-xl bg-card/30 border border-border/30 text-center text-muted-foreground">
                Aucune partie dans l'historique
              </div>
            ) : (
              <div className="space-y-2">
                {history.map((item) => (
                  <div 
                    key={item.id}
                    className="p-3 rounded-lg bg-card/20 border border-border/20 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{GAME_EMOJIS[item.game_id] || '🎮'}</span>
                      <div>
                        <div className="text-sm font-medium text-foreground">
                          {GAME_NAMES[item.game_id] || item.game_id}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(item.played_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </div>
                      </div>
                    </div>
                    {item.result && (
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        item.result === 'win' 
                          ? 'bg-green-500/20 text-green-400' 
                          : item.result === 'loss'
                          ? 'bg-red-500/20 text-red-400'
                          : 'bg-gray-500/20 text-gray-400'
                      }`}>
                        {item.result === 'win' ? 'Victoire' : item.result === 'loss' ? 'Défaite' : 'Égalité'}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </>
  );
};

export default Profile;
